import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { DashboardContractEntry, DashboardSectionData } from './sustainability-dashboard.models';


@Injectable({ providedIn: 'root' })
export class SustainabilityDashboardService {
  private readonly _contracts$ = new BehaviorSubject<DashboardContractEntry[] | null>(null);
  private readonly _selectedContractId$ = new BehaviorSubject<string | null>(null);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  private _selectedContractName = '';

  readonly loading$: Observable<boolean> = this._loading$.asObservable();
  readonly contracts$ = this._contracts$.asObservable();

  readonly selectedContractName$: Observable<string> = combineLatest([
    this._contracts$,
    this._selectedContractId$,
  ]).pipe(
    map(([contracts, selectedId]) => {
      if (!contracts?.length || !selectedId) return '';
      return contracts.find((c) => c.contract.id === selectedId)?.contract.name ?? '';
    })
  );

  get<K extends keyof DashboardSectionData>(key: K): Observable<DashboardSectionData[K] | null> {
    return combineLatest([this._contracts$, this._selectedContractId$]).pipe(
      map(([contracts, selectedId]) => {
        if (!contracts?.length) return null;
        const contract = selectedId
          ? (contracts.find((c) => c.contract.id === selectedId) ?? contracts[0])
          : contracts[0];
        const sectionData = contract?.data?.[0];
        return sectionData ? sectionData[key] : null;
      })
    );
  }

  selectContract(id: string, name?: string | null): void {
    this._selectedContractId$.next(id);
    if (name !== undefined && name !== null) {
      this._selectedContractName = name;
      return;
    }
    const contracts = this._contracts$.value;
    this._selectedContractName =
      contracts?.find((c) => c.contract.id === id)?.contract.name ?? '';
  }

  selectedContractId(): string | null {
    return this._selectedContractId$.value;
  }

  selectedContractName(): string {
    if (this._selectedContractName) {
      return this._selectedContractName;
    }
    const contracts = this._contracts$.value;
    const selectedId = this._selectedContractId$.value;
    if (!contracts?.length || !selectedId) {
      return '';
    }
    return contracts.find((c) => c.contract.id === selectedId)?.contract.name ?? '';
  }

  hasData(): boolean {
    return !!this._contracts$.value;
  }

  setLoading(value: boolean): void {
    this._loading$.next(value);
  }

  setData(contracts: DashboardContractEntry[]): void {
    this._contracts$.next(contracts);
    if (!this._selectedContractId$.value && contracts.length) {
      this._selectedContractId$.next(contracts[0].contract.id);
      this._selectedContractName = contracts[0].contract.name ?? '';
    }
  }

  reset(): void {
    this._contracts$.next(null);
    this._selectedContractId$.next(null);
    this._selectedContractName = '';
  }
}
